import { createStore } from 'vuex'
import { getPrincipal, logout } from '@/api/auth'
import {
  clearSSOPending,
  isAuthStateValid,
  readAuthState,
  writeAuthState,
  clearAuthState,
} from '@/utils/login'

// Hydrate principal+username from localStorage cache once at module load.
// We need BOTH state fields populated up front so that opening the SPA in
// a new tab (e.g. user copy-pasted the /home URL) renders the username
// immediately, without waiting for a re-fetch.
const hydratedPrincipal = (() => {
  const cached = readAuthState()
  return isAuthStateValid(cached) ? cached.data : null
})()

/**
 * Extract a human-friendly username out of the principal payload.
 * Backend shape may vary, so we try a few common fields.
 */
const pickUsername = (principal) => {
  if (!principal) return ''
  const claims = principal.claims || {}
  return (
    // Prefer claims-level identifiers (these come from the ID token).
    // claims.name is the human-friendly display name (e.g. "Chen Fang"),
    // so we use it first.
    claims.name
    || claims.preferred_username
    || claims.preferredUsername
    || claims.username
    || claims.userName
    || claims.given_name
    || claims.email
    // Fallbacks on the top-level principal object
    || principal.preferredUsername
    || principal.preferred_username
    || principal.username
    || principal.userName
    // NOTE: principal.name on this backend is a UUID (subject id), so we
    // intentionally fall through to email before using it.
    || principal.email
    || principal.name
    || (principal.user && (principal.user.userName || principal.user.username || principal.user.name))
    || ''
  )
}

const store = createStore({
  state: {
    appTitle: 'FOD Data Insight',
    // Hydrate principal state from localStorage cache on app boot so we don't
    // bounce through SSO on every page reload while the cache is still valid.
    // username MUST be hydrated together with principalState — otherwise a
    // fresh tab (new SPA boot) shows the cached principal as authenticated
    // but renders username as empty until the next RefreshPrincipal.
    principalState: hydratedPrincipal,
    username: pickUsername(hydratedPrincipal),
  },
  getters: {
    appTitle: (s) => s.appTitle,
    principalState: (s) => s.principalState,
    username: (s) => s.username,
    isAuthenticated: (s) => !!(s.principalState && s.principalState.authenticated),
  },
  mutations: {
    SET_PRINCIPAL_STATE(state, payload) {
      state.principalState = payload || null
      state.username = pickUsername(payload)
    },
  },
  actions: {
    /**
     * Refresh principal state from backend. Returns true if authenticated.
     * The backend response is expected to contain an `authenticated` flag.
     */
    async RefreshPrincipal({ commit }) {
      try {
        const res = await getPrincipal()
        // request() unwraps to response.data, so `res` is the payload itself
        const principal = res?.data ?? res
        const authenticated = !!principal?.authenticated
        if (authenticated) {
          commit('SET_PRINCIPAL_STATE', principal)
          writeAuthState(principal)
        } else {
          commit('SET_PRINCIPAL_STATE', null)
          clearAuthState()
        }
        return authenticated
      } catch (err) {
        console.warn('[auth] Failed to fetch principal', err)
        commit('SET_PRINCIPAL_STATE', null)
        clearAuthState()
        return false
      }
    },

    /**
     * Returns true if currently authenticated.
     *
     * Resolution order (mirrors cfes-admin-frontend-vue3):
     *   1. Non-expired localStorage cache  -> trust it, hydrate state if
     *      needed, and skip the backend probe entirely. This is what
     *      prevents every page load from re-triggering SSO: as long as the
     *      cached entry is younger than AUTH_STATE_TIMEOUT (30min, the
     *      CloudIDP token lifetime), we trust it.
     *   2. Otherwise probe backend /sso/v2/principal -> live result.
     *
     * NOTE: we no longer require state.principalState.authenticated here.
     * The wrapper-level `authenticated` flag is sufficient — it is only
     * written by RefreshPrincipal when the backend confirmed auth, so a
     * valid wrapper means we WERE authenticated within the last 30min.
     */
    async GetPrincipal({ state, commit, dispatch }) {
      const cached = readAuthState()
      if (isAuthStateValid(cached)) {
        // Re-commit if either the principal OR the derived username is
        // missing from in-memory state. The username check guards against
        // an older cached entry being hydrated without a username being
        // computed (e.g. logic change, or commit path skipped).
        if (!state.principalState || !state.username) {
          commit('SET_PRINCIPAL_STATE', cached.data)
        }
        return true
      }
      return await dispatch('RefreshPrincipal')
    },

    /**
     * Logout from backend SSO and clear local state.
     *
     * Mirrors cfes-admin-frontend-vue3's LogOut action: clear local
     * caches/state immediately, then fire POST /logout via axios. The
     * caller (layout) handles router.replace('/') in its finally block,
     * so we don't need a full-page navigation here.
     */
    LogOut({ commit }) {
      clearSSOPending()
      clearAuthState()
      commit('SET_PRINCIPAL_STATE', null)

      return logout()
        .then(() => {
          console.log('[auth] Logout request successful')
        })
        .catch((err) => {
          // Even if the backend call fails, local state is already cleared
          // and the caller will navigate to '/'. Just log and swallow.
          console.warn('[auth] Logout request failed', err)
        })
    },
  },
})

export default store
